/* eslint-disable max-statements */
import {SagaIterator} from 'redux-saga';
import {call, select, cancel, race, take} from 'redux-saga/effects';
import {pushDataLayer} from '@optimaxdev/analytics/mobile';

import {LOCATION_CHANGED} from 'constants/actions';
import {waitForPDP, getProductCategory} from 'sagas/EEC/product/pageview';
import {getParentProduct} from 'selectors/product/product';
import {productsToEECDetailProducts} from 'libs/EEC/convertData/productsToEECDetailProducts';
import {sendEECProductEvent} from 'libs/impression/EECImpression';
import {getBadge, getFrameUrl} from 'libs/EEC/productToEECProduct';
import {PAGE_NAME_FOR_VIZURY, pushVizury} from 'libs/analytics/vizury';
import {ProductType} from '@page/wishlist/types';
import {OptionType, EecProductDetailType} from 'libs/EEC/productToEECProductType.js';
import {getIsTryOnVisible} from 'selectors/tryOnVisibility/tryOnVisibility';
import {getCurrentPage} from 'selectors/app/app';

export type EcommerceType = {
    currencyCode: string;
    detail: {
        actionField: {
            list: string;
        };
        products: Array<{}>;
    };
};

/**
 * Sends analytics depending on the value "changeType"
 * @param {EcommerceType} ecommerce - dataset for sending analytics
 * @param {string} changeType - will return the change type or empty string
 */
export function* sendAnalyticsBeforeGetProducts(
    ecommerce: EcommerceType,
    changeType: string,
): SagaIterator {
    if (changeType) {
        yield call(pushDataLayer, {
            event: changeType === 'Color' ? 'EEC_PDP_ColorChanged' : 'EEC_product_SizeChanged',
            ecommerce,
        });
    } else {
        yield call(sendEECProductEvent, ecommerce);
    }
}

/**
 * Sends analytics on perfect Try On
 * @param {EcommerceType} ecommerce - dataset for sending analytics
 */
export function* sendAnalyticsOnPerfectTryOn(ecommerce: EcommerceType): SagaIterator {
    const {page} = yield select(getCurrentPage);

    yield call(pushDataLayer, {
        event: 'EEC_TryOn_ColorChanged',
        ecommerce,
        PlaceOfInteraction: page === 'product' ? 'Product' : 'Category',
    });
}

/**
 * Cancel the saga if location changed
 */
function* cancelSagaOnLocationChange() {
    yield take(LOCATION_CHANGED);
    yield cancel();
}

/**
 * Create object with data for EEC_PDP_Product event
 * @param {SimpleProductType} simpleProduct - product data
 * @param {boolean} changeType - will return the change type or remain empty string
 * @param {Omit<OptionType, 'index' | 'quantity' | 'sku' | 'previous_category_name'>} options - additional data for event
 * @return {EcommerceType} - data for event
 */
function* createDataForEecPdpProductEvent(
    simpleProduct: ProductType,
    changeType: string,
    options: Omit<OptionType, 'index' | 'quantity' | 'sku' | 'previous_category_name'>,
): EecProductDetailType {
    if (!changeType) yield call(pushVizury, PAGE_NAME_FOR_VIZURY.PRODUCT, simpleProduct);

    return [simpleProduct].map(product => {
        let additionalDimensions: null | {[key: string]: string} = null;

        if (!changeType) {
            additionalDimensions = {
                dimension41: getBadge(product),
                dimension44: getFrameUrl(product),
            };
        }

        const productItemEEC = productsToEECDetailProducts(product, {
            ...options,
            index: 0,
        });

        return {
            ...productItemEEC,
            ...additionalDimensions,
        };
    });
}

/**
 * @param {EcommerceType} ecommerce - dataset for sending analytics
 * @param {string} changeType - will return the change type or empty string
 */
export function* sendAnalyticEECEvent(ecommerce: EcommerceType, changeType = ''): SagaIterator {
    const isTryOn = yield select(getIsTryOnVisible);
    if (isTryOn) {
        yield call(sendAnalyticsOnPerfectTryOn, ecommerce);
    } else {
        yield call(sendAnalyticsBeforeGetProducts, ecommerce, changeType);
    }
}

/**
 * Get cart products
 * @param {string} changeType - will return the change type or remain empty string
 */
export function* getProductsECCData(changeType = ''): SagaIterator {
    // if a new route has opened during the data fetching for the current product
    // stop the EEC sending about this saga
    const [[simpleProduct, reviews, colorsSizesList]] = yield race([
        call(waitForPDP),
        call(cancelSagaOnLocationChange),
    ]);
    const {name, path} = yield call(getProductCategory);
    const {manufacturerValue} = simpleProduct;
    const {name: parentName, brand, type} = yield select(getParentProduct);

    const options = {
        category: changeType ? 'Non category page' : name,
        list: path,
        reviews,
        colorsSizesList,
        manufacturerValue,
        parentName,
        brand,
        type,
    };

    const products = yield call(
        createDataForEecPdpProductEvent,
        simpleProduct,
        changeType,
        options,
    );

    const ecommerce = {
        currencyCode: 'USD',
        detail: {
            actionField: {
                list: path,
            },
            products,
        },
    };
    yield call(sendAnalyticEECEvent, ecommerce, changeType);
}
